<script context="module">
    import { client, gql } from "./GraphQL.svelte";

    //
    // FRAGMENTS
    //

    export const FIELDS_CONTENT_FRAGMENT = `
        id,
        allowedAttachments,
        conditionDescription,
        description,
        formulaType {
            id,
            name,
            operation,
            type,
            variable {
                name,
                unit,
                value
            },
            parameterTemplate {
                arguments {
                    name,
                    unit,
                    value
                }
            }
        }
        multiplier,
        name,
        parameters {
            arguments {
                name, unit, value
            }
        }
    `;

    //
    // GET
    //

    const ALL_PROJECTS_QUERY = gql`
        query {
            projects { id, title }
        }
    `;

    const PROJECT_FIELDS_QUERY = gql`
        query($projectId: String!) {
            projectFields(projectId: $projectId) {
                ${ FIELDS_CONTENT_FRAGMENT }
            }
        }
    `;

    // TODO
    const PROJECT_DETAILS_QUERY = gql`
        query($id: Guid!) {
            project(id: $id) {
                id
            }
        }
    `;

    export function allProjectFields(projectId) {
        return client.query({
            query: PROJECT_FIELDS_QUERY,
            fetchPolicy: "network-only",
            variables: {
                projectId
            }
        });
    }

    export function allProjectTags(projectId) {
        return client.query({
            query: PROJECT_TAGS_QUERY,
            variables: {
                projectId
            }
        });
    }

    export function allProjects() {
        return client.query({
            query: ALL_PROJECTS_QUERY
        });
    }

    const ALL_TEMPLATE_FIELDS_QUERY = gql`
        query{
          templateFields{
            allowedAttachments
            conditionDescription
            description
            formulaType {
                id,
                name,
                operation,
                type,
                variable {
                    name,
                    unit,
                    value
                },
                parameterTemplate {
                    arguments {
                        name,
                        unit,
                        # TODO: the value results in NULL-errors
                        # value
                    }
                }
            }
            id
            multiplier
            name
            parameters {
              arguments {
                name,
                unit,
                value
              }
            }
          }
        }
    `;

    export function allTemplateFields() {
        return client.query({
            query: ALL_TEMPLATE_FIELDS_QUERY
        });
    }

    //
    // CREATE
    //

    const NEW_PROJECT_MUTATION = gql`
        mutation($project: AddProjectInput!, $companyId: Guid!) {
            addProject(project: $project, companyId: $companyId) {
                id,
                administrationPeriodType,
                allowBackTracking,
                companySharePercentage,
                companySharePrice,
                companyValuation,
                creationTime,
                isClosed,
                isSettled,
                settlementDateActual,
                settlementDateTarget,
                title,
                # TODO: create fragment, reduce duplication across allProjectFields, allTemplateFields
                fields {
                    ${ FIELDS_CONTENT_FRAGMENT }
                }
            }
        }
    `;

    export function initializeProjectFieldsFromTemplates(projectId) {
        // retrieve all fields that are predefined as templates in the BD
        return allTemplateFields()
            .then(res => {
                // then, since there is only an endpoint to create a single field at the time,
                // map the calls to create the fields into a Promise collective
                // TODO: this should be a backend call
                return Promise.all(
                    res.data.templateFields.map(templateField => {
                        // deconstruct resulting JSON to filter out __meta typing data from response
                        let { id, name, type, operation, variable} = templateField.formulaType;

                        // parameters are null and not empty, so we have to instantiate them
                        let parameters = templateField.parameters || {arguments: []};
                        let parameterTemplate = templateField.formulaType.parameterTemplate || {arguments: []};

                        // deconstruct arguments, strip meta data
                        let fixArguments = (a) => {
                            return a.map(arg => {
                                let {name, unit, value} = arg;
                                return {name, unit, value};
                            })
                        };

                        // here we take a templatefield and copy it into the expected structure of the project field.
                        // because the graphql response contains metadata keys such as __typename, which are not accepted
                        // by apollo if we send it right back. There is probably some apollo command for this, to either
                        // get the clean data or to clean a struct up for sending. However, aint nobody got time for that with this deadline.
                        return addProjectField(projectId, {
                            name: templateField.name,
                            description: templateField.description,
                            conditionDescription: templateField.conditionDescription,
                            multiplier: templateField.multiplier,
                            allowedAttachments: ["PDF", "DOCX", "XLSX", "PPTX"],
                            parameters: {
                                arguments: fixArguments(parameters.arguments)
                            },
                            formulaType: {
                                id,
                                name,
                                type,
                                parameterTemplate: {
                                    arguments: fixArguments(parameterTemplate.arguments)
                                },
                                operation,
                                variable
                            }
                        });
                    })
                );
            })
    }

    export function addAllProjectFields(projectId, fields) {
        return Promise.all(
            fields.map(field => addProjectField(projectId, field))
        );
    }

    export function createProject(companyId, fields, projectData) {
        return client.mutate({
            mutation: NEW_PROJECT_MUTATION,
            variables: {
                companyId: companyId,
                project: projectData
            }
        })
        .then(res => {
            console.log("[Participaid] Created project. Initializing default fields...");
            console.log(res);

            /*return initializeProjectFieldsFromTemplates( res.data.addProject.id )
                .then(res2 => {
                    console.log("[Participaid] Initialized default fields for project");
                    console.log(res2);
                    return res;
                })*/

            return addAllProjectFields(res.data.addProject.id, fields).then(res2 => {
                console.log("[Participaid] Initialized default fields for project");
                console.log(res2);
                return res;
            })
        });
    }

    const NEW_PROJECT_FIELD_MUTATION = gql`
        mutation($field: AddFieldInput!, $projectId: Guid!) {
            addProjectField(field: $field, projectId: $projectId) {
                id
            }
        }
    `;

    export function addProjectField(projectId, field) {
        return client.mutate({
            mutation: NEW_PROJECT_FIELD_MUTATION,
            variables: {
                projectId, field
            }
        });
    }

    const NEW_ENTRY_MUTATION = gql`
        mutation($contribution: AddContributionInput!, $projectId: Guid!, $projectFieldId: Guid!) {
            addContribution(contribution: $contribution, projectId: $projectId, projectFieldId: $projectFieldId) {
                id,
                review {
                    acceptanceStatus,
                    dateOfReview,
                    reasonForRejection
                },
                inputValue {
                    name, unit, value
                }
            }
        }
    `;

    export const ENTRIES_FRAGMENT = `
        id,
        project { id },
        contributor {
            id,
            title,
            user {
                id,
                firstName,
                lastName
            }
        },
        description,
        # the day that the entry was made
        entryDate,
        # the day the work wa done that this entry refers to
        contributionDate,
        # the time of creation of this entry
        creationTime,
        tags,
        inputValue {
            name, unit, value
        },
        projectField {
            conditionDescription,
            description,
            id,
            multiplier,
            formulaType {
                type,
                parameterTemplate {
                    arguments {
                        name,
                        unit,
                        # TODO: the value results in NULL-errors
                        # value
                    }
                }
            },
            name,
            parameters {
                arguments {
                    name, unit, value
                }
            }
            #allowedAttachments
        },
        review {
            acceptanceStatus,
            dateOfReview,
            reasonForRejection
        }
    `;

    const UPDATE_ENTRY_MUTATION = gql`
        mutation($contribution: UpdateContributionInput!, $projectId: Guid!, $projectFieldId: Guid!) {
            updateContribution(contribution: $contribution, projectId: $projectId, projectFieldId: $projectFieldId) {
                ${ENTRIES_FRAGMENT}
            }
        }
    `;

    export function addContribution(projectId, projectFieldId, contribution) {
        return client.mutate({
            mutation: NEW_ENTRY_MUTATION,
            variables: {
                projectId, projectFieldId, contribution
            }
        });
    }

    export function updateContribution(projectId, projectFieldId, contribution) {
        return client.mutate({
            mutation: UPDATE_ENTRY_MUTATION,
            variables: {
                projectId, projectFieldId, contribution
            }
        });
    }

    const APPROVE_ENTRY_MUTATION = gql`
        mutation($projectId: Guid!, $contributionId: Guid!) {
            approveContribution(projectId: $projectId, contributionId: $contributionId) {
                id,
                review {
                    acceptanceStatus,
                    dateOfReview,
                    reasonForRejection
                }
            }
        }
    `;

    export function approveContribution(projectId, contributionId) {
        return client.mutate({
            mutation: APPROVE_ENTRY_MUTATION,
            variables: {
                projectId, contributionId
            }
        });
    }

    const REJECT_ENTRY_MUTATION = gql`
        mutation($projectId: Guid!, $contributionId: Guid!, $reason: String!) {
            rejectContribution(projectId: $projectId, contributionId: $contributionId, reason: $reason) {
                id,
                review {
                    acceptanceStatus,
                    dateOfReview,
                    reasonForRejection
                }
            }
        }
    `;

    export function rejectContribution(projectId, contributionId, reason) {
        return client.mutate({
            mutation: REJECT_ENTRY_MUTATION,
            variables: {
                projectId, contributionId, reason
            }
        });
    }

    const CLOSE_PROJECT_MUTATION = gql`
        mutation($id: Guid!) {
            closeAndSettleProject(id: $id) {
                id,
                isClosed,
                isSettled,
                settlementDateActual
            }
        }
    `;

    export function closeProject(id) {
        return client.mutate({
            mutation: CLOSE_PROJECT_MUTATION,
            variables: {
                id
            }
        });
    }

    // TODO: this query seems redundant,
    // with it we can download a "report", but how can the API know whether
    // an uploaded attachment is an end report?
    const DOWNLOAD_REPORT_QUERY = gql`
        mutation($contributorId: Guid!, $projectId: Guid!) {
            downloadReport(contributorId: $contributorId, projectId: $projectId) {
                url,
                id,
                fileType,
                contributions #actually single contribution linked to the attachment
            }
        }
    `;

    export function downloadReport(contributorId, projectId) {
        return client.query({
            query: DOWNLOAD_REPORT_QUERY,
            variables: {
                contributorId, projectId
            }
        });
    }

    export function downloadPersonalReport() { alert("TODO") }
    export function settlePeriod() { alert("TODO") }

    //
    // DELETE
    //

    const DELETE_PROJECT_MUTATION = gql`
        mutation($id: Guid!) {
            deleteProject(id: $id) {
                id, creationTime
        }}
    `;

    export function deleteProject(id) {
        if (!id) {
            throw "deleteProject expects argument!"
        }
        return client.mutate({
            mutation: DELETE_PROJECT_MUTATION,
            variables: {
                id
            }
        });
    }

    export function deleteAllProjects() {
        return client.query({
            query: gql`
                query {
                    me {
                        contributingProjects {
                            id
                        }
                    }
                }
            `
        })
        .then(res => {
            res.data.me.contributingProjects.forEach(project => deleteProject(project.id));
        })
    }

</script>